import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, FormLabel, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { renderEnterpriseTree } from "../hook/useEnterpriseTree";
import { sortedUsers } from '../utils/util';
import { User } from "../type/user";
import { Enterprise } from "../type";

interface UserSelectionProps {
    title: string;
    isChecked?: boolean;
    checkBoxClick?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    selectedEnterpriseUsers: User[];
    setSelectedEnterpriseUsers: React.Dispatch<React.SetStateAction<User[]>>;
    selectedIndependentUsers: User[];
    setSelectedIndependentUsers: React.Dispatch<React.SetStateAction<User[]>>;
    enterpriseUsers: User[];
    setEnterpriseUsers: React.Dispatch<React.SetStateAction<User[]>>;
    independentUsers: User[];
    setIndependentUsers: React.Dispatch<React.SetStateAction<User[]>>;
    enterprises: Enterprise[];
    showTargetToggle?: boolean;
    editable?: boolean;
}

export const UserSelection = ({
    title,
    isChecked = true,
    checkBoxClick,
    selectedEnterpriseUsers,
    setSelectedEnterpriseUsers,
    selectedIndependentUsers,
    setSelectedIndependentUsers,
    enterpriseUsers,
    setEnterpriseUsers,
    independentUsers,
    setIndependentUsers,
    enterprises,
    showTargetToggle = true,
    editable = checkBoxClick !== undefined,
}: UserSelectionProps) => {
    return (
        <>   
          {showTargetToggle && (
          <FormControl 
            component="fieldset"
            sx={{ 
              border: 1, 
              borderColor: '1px solid #ccc',
              borderRadius: 1, 
              p: 1
            }}
          >
            <FormLabel component="legend" sx={{ fontSize: '0.75rem', px: 1 }}>{title}</FormLabel>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={checkBoxClick}
                    disabled={checkBoxClick ? false : true}
                  />
                }
                label="전체 사용자"
              />

              {isChecked ? (
                <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mt: 0.5 }}>
                  전체 사용자에게 알림을 보냅니다.
                </Typography>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mt: 0.5 }}>
                  선택된 사용자에게 알림을 보냅니다.
                </Typography>
              )}
            </FormGroup>
          </FormControl>
          )}

          {!isChecked && (
              <>
              <Box
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 3, 
                  mt: 3,
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  padding: 2,
                }}>
                {/* 미선택 사용자 */}
                {editable && (
                    <>
                    <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>미선택 개인 사용자</Typography>

                        <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                            setSelectedIndependentUsers(prev => sortedUsers([...prev, ...independentUsers]));
                            setIndependentUsers([]);
                        }}
                        >
                        전체 선택
                        </Button>
                    </Box>
                    <Table size="small">
                        <TableBody>
                        {independentUsers.map(user => (
                            <TableRow key={user.uid}>
                            <TableCell>{user.displayName}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.phoneNumber}</TableCell>
                            <TableCell align="right">
                                <Button
                                size="small"
                                sx={{ minWidth: '60px', padding: '4px 8px' }}
                                onClick={() => {
                                    setIndependentUsers(sortedUsers(independentUsers.filter(u => u.uid !== user.uid)));
                                    setSelectedIndependentUsers(sortedUsers([...selectedIndependentUsers, user]));
                                }}>선택</Button>
                            </TableCell>
                            </TableRow>
                        ))}
                        </TableBody>
                    </Table>
                    </Box>

                    <Divider sx={{ borderBottomWidth: '2px', borderColor: '#444' }} />
                    </>
                )}
                {/* 선택된 사용자 */}
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>선택된 개인 사용자</Typography>
                    {editable && selectedIndependentUsers.length > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setIndependentUsers(prev => sortedUsers([...prev, ...selectedIndependentUsers]));
                          setSelectedIndependentUsers([]);
                        }}
                      >
                        전체 미선택
                      </Button>
                    )}
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {selectedIndependentUsers.map(user => (
                        <TableRow key={user.uid}>
                          <TableCell>{user.displayName}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.phoneNumber}</TableCell>
                        <TableCell align="right">
                          {editable && (
                            <Button
                                size="small"
                                sx={{ minWidth: '60px', padding: '4px 8px' }}
                                onClick={() => {
                                    setSelectedIndependentUsers(sortedUsers(selectedIndependentUsers.filter(u => u.uid !== user.uid)));
                                    setIndependentUsers(sortedUsers([...independentUsers, user]));
                                }}>
                                미선택
                            </Button>
                          )}
                        </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>

              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 3, 
                  mt: 3,
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  padding: 2,
                }}>
                {editable && (
                    <>
                    <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>미선택 기업 사용자</Typography>
                    {renderEnterpriseTree(enterpriseUsers, enterprises, false, selectedEnterpriseUsers, setSelectedEnterpriseUsers, enterpriseUsers, setEnterpriseUsers, false)}
                    </Box>

                    <Divider sx={{ borderBottomWidth: '2px', borderColor: '#444' }} />
                    </>
                )}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>선택된 기업 사용자</Typography>
                  {renderEnterpriseTree(selectedEnterpriseUsers, enterprises, true, selectedEnterpriseUsers, setSelectedEnterpriseUsers, enterpriseUsers, setEnterpriseUsers, false)}
                </Box>
              </Box>
              </>
            )}
        </>
    );
}
